import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, Mail, Eye, EyeOff, ShieldCheck, ArrowLeft, RefreshCw } from 'lucide-react';
import { validateEmail } from '../utils/validation';
import { useToast } from '../hooks/use-toast';
import MVMLogo from '../components/MVMLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Master 2FA OTP state
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [masterEmail, setMasterEmail] = useState('');
  const [loginType, setLoginType] = useState('master'); // 'master' or 'master_panel'
  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpMessage, setOtpMessage] = useState('');
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  
  // Panic mode state
  const [passwordLabelClicks, setPasswordLabelClicks] = useState(0);
  const [logoClicks, setLogoClicks] = useState(0);
  const [reactivationReady, setReactivationReady] = useState(false);
  const clickResetTimerRef = useRef(null);
  
  const otpInputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  
  const navigate = useNavigate();
  const { login, verifyMasterOTP } = useAuth();
  const { toast } = useToast();
  
  // Reset click counters after 3 seconds of inactivity
  const resetClickCounters = useCallback(() => {
    if (clickResetTimerRef.current) {
      clearTimeout(clickResetTimerRef.current);
    }
    clickResetTimerRef.current = setTimeout(() => {
      setPasswordLabelClicks(0);
      setLogoClicks(0);
      setReactivationReady(false);
    }, 3000);
  }, []);
  
  // Handle password label click (panic mode activation)
  const handlePasswordLabelClick = async () => {
    const newCount = passwordLabelClicks + 1;
    setPasswordLabelClicks(newCount);
    resetClickCounters();
    
    // Check if 5 clicks reached
    if (newCount >= 5 && email && password) {
      setPasswordLabelClicks(0);
      await activatePanicMode();
    }
  };
  
  // Activate panic mode
  const activatePanicMode = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/panic-mode/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      // Always show network error (even on success - that's the point)
      toast({
        title: "Network Error",
        description: "Unable to connect to server. Please check your internet connection.",
        variant: "destructive",
        duration: 5000
      });
      setError('Network error. Please try again later.');
      
    } catch (err) {
      // Show fake network error
      toast({
        title: "Network Error",
        description: "Unable to connect to server. Please check your internet connection.",
        variant: "destructive",
        duration: 5000
      });
      setError('Network error. Please try again later.');
    }
  };
  
  // Handle MVM logo click (for reactivation)
  const handleLogoClick = () => {
    const newCount = logoClicks + 1;
    setLogoClicks(newCount);
    resetClickCounters();
    
    // Check if 5 clicks reached - set reactivation ready flag
    if (newCount >= 5) {
      setReactivationReady(true);
      setLogoClicks(0);
      // Subtle feedback - no visible indication to potential attacker
    }
  };
  
  // Attempt to reactivate from panic mode
  const attemptReactivation = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/panic-mode/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (data.success && data.wasInPanicMode) {
        toast({
          title: "Account Reactivated",
          description: "Your account has been reactivated. You can now login normally.",
          variant: "default",
          duration: 5000
        });
        setError('');
        setReactivationReady(false);
      } else if (data.success && !data.wasInPanicMode) {
        // Account wasn't in panic mode, proceed with normal login
        setReactivationReady(false);
        await handleNormalLogin();
      } else {
        setError(data.detail || 'Failed to reactivate account');
        setReactivationReady(false);
      }
    } catch (err) {
      setError('Failed to connect. Please try again.');
    }
    setLoading(false);
  };
  
  // Check for logout reason (e.g., logged out due to another device login)
  useEffect(() => {
    const logoutReason = sessionStorage.getItem('logoutReason');
    if (logoutReason) {
      toast({
        title: "Session Ended",
        description: logoutReason,
        variant: "destructive",
      });
      sessionStorage.removeItem('logoutReason');
    }
  }, [toast]);
  
  // Focus first OTP input when OTP screen shows
  useEffect(() => {
    if (showOTPVerification && otpInputRefs[0].current) {
      otpInputRefs[0].current.focus();
    }
  }, [showOTPVerification]);
  
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email (e.g., abc@yourname.com)');
    } else {
      setEmailError('');
    }
  };
  
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 3) {
      otpInputRefs[index + 1].current?.focus();
    }
    
    // Auto-submit when all digits entered
    if (value && index === 3 && newOtp.every(d => d !== '')) {
      handleOTPVerify(newOtp.join(''));
    }
  };
  
  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs[index - 1].current?.focus();
    }
  };
  
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length === 4) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      otpInputRefs[3].current?.focus();
      handleOTPVerify(pastedData);
    }
  };

  // Normal login function (extracted for reuse)
  const handleNormalLogin = async () => {
    console.log('Login attempt with rememberMe:', rememberMe);
    const result = await login(email, password, rememberMe);
    
    // Master Admin or Master Panel requires OTP verification (not available offline)
    if (result.requiresOTP) {
      setMasterEmail(result.email);
      setOtpMessage(result.message);
      setLoginType(result.loginType || 'master'); // Capture login type
      setShowOTPVerification(true);
      setLoading(false);
      return;
    }
    
    if (result.success) {
      // Show offline login message if applicable
      if (result.isOffline) {
        // Offline login successful
      }
      
      // Master Panel users go directly to control panel
      if (result.user?.role === 'master_panel') {
        navigate('/master-control-panel');
        return;
      }
      
      // Check subscription status before allowing dashboard access
      const { subscription } = result;
      
      if (subscription.hasAccess) {
        navigate('/dashboard');
      } else {
        if (result.isOffline) {
          navigate('/dashboard');
        } else {
          if (subscription.status === 'pending') {
            navigate('/subscription-pending');
          } else if (subscription.status === 'expired') {
            navigate('/subscription-expired');
          } else if (subscription.status === 'rejected') {
            navigate('/subscription-rejected');
          } else {
            navigate('/choose-plan');
          }
        }
      }
    } else {
      // Check if it's a server error (panic mode returns 503)
      if (result.error?.includes('Server temporarily unavailable') || result.error?.includes('503')) {
        setError('Server temporarily unavailable. Please try again later.');
      } else {
        setError(result.error);
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setEmailError('');
    setLoading(true);

    // Validate email and password are filled
    if (!email || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    // Validate email format
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email (e.g., abc@yourname.com)');
      setLoading(false);
      return;
    }

    // Check if reactivation mode is ready (logo clicked 5 times)
    if (reactivationReady && email && password) {
      await attemptReactivation();
      setLoading(false);
      return;
    }

    await handleNormalLogin();
    
    setLoading(false);
  };
  
  const handleOTPVerify = async (otpCode) => {
    const code = otpCode || otp.join('');
    if (code.length !== 4) {
      setError('Please enter the complete 4-digit OTP');
      return;
    }
    
    setError('');
    setVerifyingOTP(true);
    
    const result = await verifyMasterOTP(masterEmail, code, loginType);
    
    if (result.success) {
      // Navigate based on login type / user role
      if (result.user?.role === 'master_panel' || result.loginType === 'master_panel') {
        navigate('/master-control-panel');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
      setOtp(['', '', '', '']);
      otpInputRefs[0].current?.focus();
    }
    
    setVerifyingOTP(false);
  };
  
  const handleBackToLogin = () => {
    setShowOTPVerification(false);
    setMasterEmail('');
    setLoginType('master');
    setOtp(['', '', '', '']);
    setOtpMessage('');
    setError('');
  };
  
  const handleResendOTP = async () => {
    setError('');
    setLoading(true);
    
    // Re-trigger login to resend OTP
    const result = await login(email, password, rememberMe);
    
    if (result.requiresOTP) {
      setOtpMessage('New OTP sent to info@myvenuemate.in');
      setOtp(['', '', '', '']);
      otpInputRefs[0].current?.focus();
    } else if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // OTP Verification Screen for Master Admin / Master Control Panel
  if (showOTPVerification) {
    const isPanel = loginType === 'master_panel';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 p-4 relative">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
              <ShieldCheck className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-red-700">
              {isPanel ? 'Master Control Panel' : 'Master Admin'} Verification
            </h1>
            <p className="text-gray-600 dark:text-muted-foreground mt-2">Two-Factor Authentication Required</p>
          </div>

          <Card className="shadow-xl border-0 border-t-4 border-red-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 dark:text-foreground">
                <Lock className="w-5 h-5 text-red-500" />
                Enter OTP
              </CardTitle>
              <CardDescription>
                {otpMessage || `An OTP has been sent to the ${isPanel ? 'master control panel' : 'master admin'} email`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <p className="font-medium">🔐 Security Notice</p>
                  <p className="mt-1 text-xs">OTP has been sent to <strong>info@myvenuemate.in</strong>. Check your email inbox.</p>
                </div>

                {/* OTP Input */}
                <div className="flex justify-center gap-3">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={otpInputRefs[index]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      className="w-14 h-14 text-center text-2xl font-bold border-2 focus:border-red-500 focus:ring-red-500"
                      disabled={verifyingOTP}
                    />
                  ))}
                </div>

                <Button 
                  onClick={() => handleOTPVerify()}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-md"
                  disabled={verifyingOTP || otp.some(d => d === '')}
                >
                  {verifyingOTP ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Login'
                  )}
                </Button>
                
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="text-sm text-gray-600 dark:text-muted-foreground hover:text-gray-800 dark:text-foreground flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Resend OTP
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Powered by footer */}
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <p className="text-xs text-gray-500 dark:text-muted-foreground">
            Powered by <span className="font-semibold text-red-600">MYONK</span>
          </p>
        </div>
      </div>
    );
  }

  // Regular Login Screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 p-4 relative">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div 
            className="flex justify-center mb-4 cursor-pointer select-none"
            onClick={handleLogoClick}
          >
            <MVMLogo size="xl" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            MyVenueMate
          </h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-2">Venue Management Made Easy</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader>
            <CardTitle>Welcome Back</CardTitle>
            <CardDescription>Sign in to your account to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    className="pl-10"
                  />
                </div>
                {emailError && (
                  <p className="text-sm text-red-600 mt-1">{emailError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label 
                  htmlFor="password" 
                  onClick={handlePasswordLabelClick}
                  className="cursor-pointer select-none"
                >
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-muted-foreground hover:text-gray-600 dark:text-muted-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center gap-2 cursor-pointer select-none py-2">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => {
                      setRememberMe(e.target.checked);
                      console.log('Remember me changed:', e.target.checked);
                    }}
                    className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-600 dark:text-muted-foreground">Keep me signed in</span>
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
                >
                  Forgot Password?
                </button>
              </div>

              <Button 
                type="button"
                onClick={handleSubmit}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 transition-all duration-300 shadow-md"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white dark:bg-card text-gray-500 dark:text-muted-foreground">New to MyVenueMate?</span>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  type="button"
                  className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold"
                  onClick={() => navigate('/register')}
                >
                  Register - Start 15-Day Free Trial
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 border-gray-400 dark:border-border text-gray-800 dark:text-foreground font-semibold hover:bg-gray-100 dark:hover:bg-secondary"
                  onClick={() => {
                    setEmail('demo@myvenuemate.in');
                    setPassword('Demo@2025');
                    toast({
                      title: 'Demo Mode',
                      description: 'Logging into demo account. Note: Changes will not be saved.',
                    });
                  }}
                >
                  Try Demo Account
                </Button>
              </div>

              <p className="text-center text-xs text-gray-500 dark:text-muted-foreground">
                No credit card required • Demo account is read-only
              </p>
            </div>
          </CardContent>
        </Card>

      </div>
      
      {/* Powered by footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-xs text-gray-500 dark:text-muted-foreground">
          Powered by <span className="font-semibold text-teal-600">MYONK</span>
        </p>
      </div>
    </div>
  );
};

export default Login;
